"use client";

import React, { useState } from "react";
import { useDialog } from "@/hooks/useDialog";
import { DateTime } from "luxon";
import PropTypes from "prop-types";

/**
 * Busqueda — formulario de "Desde / Hasta" + botón Consultar.
 *
 * Esta es una de las ventanas que estaba feita en oscuro: el wrapper
 * blanco de los inputs `bg-white` se quedaba blanco en modo oscuro,
 * lo que dejaba dos cajas blancas brillantes sobre fondo oscuro.
 *
 * Ahora cada elemento usa los tokens semánticos.
 *
 * IMPORTANTE: el `<input type="date">` dibuja un widget nativo (el ícono
 * del calendario). Para que ese ícono se vea correctamente en oscuro
 * agregamos `color-scheme: dark` en el .dark global (ver style.css),
 * con eso el navegador invierte por sí solo el control nativo.
 */
const Busqueda = ({ isLoading, onSearch }) => {
  const { show } = useDialog();
  const today = DateTime.now().toSQLDate();
  const year = today.split("-")[0];

  const [fechaE, setFechaE] = useState(`${year}-01-01`);
  const [fechaV, setFechaV] = useState(today);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!fechaE?.trim()) {
      show({
        type: "warning",
        title: "",
        message: "Por favor, ingresa una fecha de inicio válida",
      });
      return;
    }

    if (!fechaV?.trim()) {
      show({
        type: "warning",
        title: "",
        message: "Por favor, ingresa una fecha de fin válida",
      });
      return;
    }

    if (fechaE > fechaV) {
      show({
        type: "warning",
        title: "Rango de fechas inválido",
        message: "La fecha desde no puede ser mayor que la fecha hasta.",
      });
      return;
    }

    await onSearch(fechaE, fechaV);
  };

  // Clases del "wrapper" del input. Lo factorizo para mantener consistencia
  // entre los dos campos (Desde / Hasta) y para que cualquier ajuste
  // futuro se haga en un solo lugar.
  const inputWrapperClass =
    "flex w-full items-center rounded-lg border px-3 py-2 sm:w-auto " +
    "bg-surface dark:bg-dark-surface " +
    "border-border-default dark:border-dark-border-default " +
    "focus-within:border-primary dark:focus-within:border-dark-primary " +
    "transition-colors";

  // Clases del input nativo. `bg-transparent` hace que el wrapper sea quien
  // dibuja el fondo (más fácil controlar el contraste y el focus ring).
  const inputClass =
    "ml-2 w-full bg-transparent outline-none " +
    "text-text-default dark:text-dark-text-default " +
    "placeholder:text-text-soft dark:placeholder:text-dark-text-soft";

  return (
    // Card contenedora del form. Le añadimos un padding interno y los
    // tokens semánticos. Antes esto era un <form> "pelado" y por eso
    // visualmente no se diferenciaba del fondo en oscuro.
    <form
      className="flex w-full flex-col gap-3 rounded-md border p-4
                 bg-surface dark:bg-dark-surface
                 border-border-default dark:border-dark-border-default
                 shadow-default dark:shadow-none
                 sm:flex-row sm:items-end sm:gap-4"
      onSubmit={handleSubmit}
    >
      {/* Campo: Desde */}
      <div className="flex w-full flex-col gap-1 sm:w-auto">
        <label
          htmlFor="fecha-desde"
          className="text-sm font-medium text-text-muted dark:text-dark-text-muted"
        >
          Desde
        </label>
        <div className={inputWrapperClass}>
          <input
            id="fecha-desde"
            disabled={isLoading}
            type="date"
            value={fechaE}
            onChange={(e) => setFechaE(e.target.value)}
            required
            className={inputClass}
          />
        </div>
      </div>

      {/* Campo: Hasta */}
      <div className="flex w-full flex-col gap-1 sm:w-auto">
        <label
          htmlFor="fecha-hasta"
          className="text-sm font-medium text-text-muted dark:text-dark-text-muted"
        >
          Hasta
        </label>
        <div className={inputWrapperClass}>
          <input
            id="fecha-hasta"
            disabled={isLoading}
            type="date"
            value={fechaV}
            onChange={(e) => setFechaV(e.target.value)}
            required
            className={inputClass}
          />
        </div>
      </div>

      {/* Botón. `btn-primary` de DaisyUI ya cambia bien con data-theme,
         pero le añadimos `text-white` para asegurarnos de que el texto
         se ve sobre el azul en ambos temas. */}
      <button
        className="btn btn-primary w-full rounded-lg text-white sm:w-auto"
        disabled={isLoading}
        type="submit"
      >
        {isLoading && <span className="loading loading-spinner"></span>}
        Consultar
      </button>
    </form>
  );
};

export default Busqueda;

Busqueda.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onSearch: PropTypes.func.isRequired,
};
